import React from "react";
import "./DownloadPage.css";
import SEOHead from "../../common/SEOHead/SEOHead";

const DownloadPage = () => {
  return (
    <div className="download-page">
      <SEOHead
        title="Download GoalFlex App - Free Goal Setting & Achievement Platform"
        description="Download the GoalFlex app for iOS and Android. Start achieving your goals with AI-powered planning, smart tracking, and intelligent insights. Free download available now."
        keywords="download goalflex, goalflex app, iOS app, Android app, goal setting app, free download, mobile app"
        url="https://goalflex.net/download"
      />
      {/* Hero Section */}
      <section className="download-hero">
        <div className="container">
          <h1>Download GoalFlex</h1>
          <p>
            Get the official GoalFlex app and start playing soccer with verified
            players in your area. Available on iOS and Android.
          </p>

          {/* Download Buttons */}
          <div className="download-buttons">
            <a
              href="https://apps.apple.com/us/app/goalflex/id6745612755"
              target="_blank"
              rel="noopener noreferrer"
              className="download-btn ios-btn"
            >
              <img
                src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                alt="Download on App Store"
              />
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.goalflex.app&pcampaignid=web_share"
              target="_blank"
              rel="noopener noreferrer"
              className="download-btn android-btn"
            >
              <img
                src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                alt="Get it on Google Play"
              />
            </a>
          </div>

          {/* Download Stats */}
          <div className="download-stats">
            <div className="stat">
              <span className="stat-number">1K+</span>
              <span className="stat-label">Downloads</span>
            </div>
            <div className="stat">
              <span className="stat-number">4.8</span>
              <span className="stat-label">App Store Rating</span>
            </div>
            <div className="stat">
              <span className="stat-number">4.7</span>
              <span className="stat-label">Google Play Rating</span>
            </div>
          </div>
        </div>
      </section>

      {/* App Features */}
      <section className="app-features">
        <div className="container">
          <h2>Why Download GoalFlex?</h2>
          <div className="features-grid">
            <div className="feature-item">
              <div className="feature-icon">📱</div>
              <h3>Easy to Use</h3>
              <p>
                Intuitive interface designed for soccer players. Find and join
                games in just a few taps.
              </p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">🏆</div>
              <h3>Verified Players</h3>
              <p>
                Play with confidence knowing all players are verified through
                our secure registration process.
              </p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">📍</div>
              <h3>Location-Based</h3>
              <p>
                Find games happening near you with our smart location-based game
                discovery.
              </p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">💳</div>
              <h3>Secure Payments</h3>
              <p>
                Safe and secure in-app payments. No more awkward cash
                collections at the field.
              </p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">⚽</div>
              <h3>Quality Venues</h3>
              <p>
                Access to premium soccer fields and facilities in your area.
              </p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">👥</div>
              <h3>Community</h3>
              <p>
                Connect with local soccer enthusiasts and build lasting
                friendships on and off the field.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* App Screenshots */}
      <section className="app-screenshots">
        <div className="container">
          <h2>See GoalFlex in Action</h2>
          <div className="screenshots-grid">
            <div className="screenshot">
              <img
                src="https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"
                alt="Game Discovery"
              />
              <h4>Discover Games</h4>
              <p>Browse available games in your area</p>
            </div>
            <div className="screenshot">
              <img
                src="https://images.unsplash.com/photo-1461749280684-dccba630e2f6?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"
                alt="Player Profiles"
              />
              <h4>Player Profiles</h4>
              <p>View detailed player profiles and ratings</p>
            </div>
            <div className="screenshot">
              <img
                src="https://images.unsplash.com/photo-1574629810360-7efbbe195018?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"
                alt="Book Games"
              />
              <h4>Book & Pay</h4>
              <p>Secure booking and payment system</p>
            </div>
          </div>
        </div>
      </section>

      {/* System Requirements */}
      <section className="system-requirements">
        <div className="container">
          <h2>System Requirements</h2>
          <div className="requirements-grid">
            <div className="requirement-item">
              <h3>📱 iOS</h3>
              <ul>
                <li>iOS 12.0 or later</li>
                <li>iPhone 6s or newer</li>
                <li>50MB storage space</li>
                <li>Internet connection required</li>
              </ul>
            </div>
            <div className="requirement-item">
              <h3>🤖 Android</h3>
              <ul>
                <li>Android 7.0 (API level 24) or higher</li>
                <li>2GB RAM minimum</li>
                <li>50MB storage space</li>
                <li>Internet connection required</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="download-faq">
        <div className="container">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h4>Is the app free to download?</h4>
              <p>
                Yes, GoalFlex is free to download from both the App Store and
                Google Play Store.
              </p>
            </div>
            <div className="faq-item">
              <h4>Do I need to pay to join games?</h4>
              <p>
                Game fees vary depending on the venue and organizer. Most games
                have a small fee to cover field costs.
              </p>
            </div>
            <div className="faq-item">
              <h4>Can I use the app without creating an account?</h4>
              <p>
                You need to create an account to join games, but you can browse
                available games without signing up.
              </p>
            </div>
            <div className="faq-item">
              <h4>Is my payment information secure?</h4>
              <p>
                Yes, we use industry-standard encryption and secure payment
                processors to protect your information.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="download-cta">
        <div className="container">
          <h2>Ready to Play?</h2>
          <p>
            Join thousands of soccer players who have already downloaded
            GoalFlex.
          </p>
          <div className="download-buttons">
            <a
              href="https://apps.apple.com/us/app/goalflex/id6745612755"
              target="_blank"
              rel="noopener noreferrer"
              className="download-btn ios-btn"
            >
              <img
                src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                alt="Download on App Store"
              />
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.goalflex.app&pcampaignid=web_share"
              target="_blank"
              rel="noopener noreferrer"
              className="download-btn android-btn"
            >
              <img
                src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                alt="Get it on Google Play"
              />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DownloadPage;
